import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TaxResidencyModal({ isOpen, onConfirm, detectedCountry, userEmail }) {
  const { t } = useTranslation();
  
  // Get country from geolocation coordinates (rough approximation)
  const getCountryFromCoords = (latitude, longitude) => {
    // Germany bounds: 47.27-55.06 N, 5.87-15.04 E
    if (latitude >= 47.27 && latitude <= 55.06 && longitude >= 5.87 && longitude <= 15.04) {
      // Check if closer to Austria
      if (latitude < 49 && longitude > 9.5) return 'AT'; // Southern-eastern Germany/Austria region
      return 'DE';
    }
    
    // Austria bounds: 46.37-49.02 N, 9.53-17.16 E  
    if (latitude >= 46.37 && latitude <= 49.02 && longitude >= 9.53 && longitude <= 17.16) {
      return 'AT';
    }
    
    // Switzerland bounds: 45.82-47.81 N, 5.96-10.49 E
    if (latitude >= 45.82 && latitude <= 47.81 && longitude >= 5.96 && longitude <= 10.49) {
      return 'CH';
    }
    
    return null; // Unknown location
  };
  
  // Try to get best country suggestion from multiple sources
  const getBestCountrySuggestion = () => {
    // 1. Check if user has granted geolocation permission
    const geoConsent = localStorage.getItem('geolocation-consent');
    const geoDataStr = localStorage.getItem('geolocation-data');
    
    if (geoConsent === 'true' && geoDataStr) {
      try {
        const geoData = JSON.parse(geoDataStr);
        const countryFromGeo = getCountryFromCoords(geoData.latitude, geoData.longitude);
        if (countryFromGeo) {
          console.log('[TaxResidencyModal] Using geolocation:', countryFromGeo);
          return countryFromGeo;
        }
      } catch (e) {
        console.error('[TaxResidencyModal] Failed to parse geolocation data:', e);
      }
    }
    
    // 2. Fallback to browser locale
    const locale = navigator.language || navigator.userLanguage || '';
    if (locale.includes('de-AT') || locale.includes('de_AT')) return 'AT';
    if (locale.includes('de-DE') || locale.includes('de_DE')) return 'DE';
    if (locale.includes('de-CH') || locale.includes('de_CH')) return 'CH';
    
    // 3. Final fallback to IP detection
    return detectedCountry || 'DE';
  };
  
  const [selectedCountry, setSelectedCountry] = useState('DE');
  const [loading, setLoading] = useState(false);

  // Update selected country when modal opens or geolocation updates
  React.useEffect(() => {
    if (isOpen) {
      const suggestedCountry = getBestCountrySuggestion();
      setSelectedCountry(suggestedCountry);
      console.log('[TaxResidencyModal] Updated suggestion:', suggestedCountry);
    }
  }, [isOpen, detectedCountry]);

  // Listen for geolocation updates
  React.useEffect(() => {
    const handleGeolocationUpdate = (event) => {
      console.log('[TaxResidencyModal] Geolocation updated:', event.detail);
      const suggestedCountry = getBestCountrySuggestion();
      setSelectedCountry(suggestedCountry);
    };

    window.addEventListener('geolocation-updated', handleGeolocationUpdate);
    return () => window.removeEventListener('geolocation-updated', handleGeolocationUpdate);
  }, []);

  console.log('[TaxResidencyModal] Browser locale:', navigator.language, 'Selected:', selectedCountry, 'IP-Detected:', detectedCountry);

  if (!isOpen) return null;

  const countries = [
    { code: 'DE', name: t('country.germany', 'Deutschland'), flag: '🇩🇪' },
    { code: 'AT', name: t('country.austria', 'Österreich'), flag: '🇦🇹' },
    { code: 'CH', name: t('country.switzerland', 'Schweiz'), flag: '🇨🇭' },
    { code: 'OTHER', name: t('country.other', 'Anderes Land'), flag: '🌍' }
  ];

  const handleConfirm = async () => {
    if (!selectedCountry) {
      alert(t('taxResidency.selectCountryFirst', 'Bitte wählen Sie ein Land aus'));
      return;
    }
    
    console.log('[TaxResidencyModal] Confirming country:', selectedCountry);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      console.log('[TaxResidencyModal] Calling API with token:', token ? 'present' : 'missing');
      
      const response = await axios.post(
        `${API}/country/confirm-tax-residency`,
        null,
        {
          params: { country_code: selectedCountry },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('[TaxResidencyModal] API response:', response.data);
      
      // Call onConfirm callback - this should close the modal
      console.log('[TaxResidencyModal] Calling onConfirm callback');
      if (onConfirm) {
        try {
          await onConfirm(selectedCountry);
          console.log('[TaxResidencyModal] onConfirm callback completed');
        } catch (callbackError) {
          console.error('[TaxResidencyModal] onConfirm callback failed:', callbackError);
          // Continue anyway - API call was successful
        }
      } else {
        console.warn('[TaxResidencyModal] No onConfirm callback provided!');
      }
      
      // Reset loading state after everything
      setLoading(false);
      
    } catch (error) {
      console.error('[TaxResidencyModal] API call failed:', error);
      console.error('[TaxResidencyModal] Error details:', error.response?.data);
      alert(t('taxResidency.errorConfirming', 'Fehler beim Bestätigen der Steueransässigkeit'));
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full my-8 p-4 sm:p-8 animate-fade-in">
        {/* Header */}
        <div className="text-center mb-4 sm:mb-6">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            {t('taxResidency.title', 'Steueransässigkeit bestätigen')}
          </h2>
          <p className="text-gray-600 text-xs sm:text-sm">
            {t('taxResidency.subtitle', 'Bitte bestätigen Sie Ihr Land der Steuerpflicht')}
          </p>
          {(() => {
            const geoConsent = localStorage.getItem('geolocation-consent');
            const hasGeolocation = geoConsent === 'true' && localStorage.getItem('geolocation-data');
            
            if (hasGeolocation) {
              return (
                <p className="text-green-600 text-xs mt-2">
                  ✓ {t('taxResidency.usingGeolocation', 'Standortdaten werden verwendet für präzise Erkennung')}
                </p>
              );
            } else if (detectedCountry && detectedCountry !== selectedCountry) {
              return (
                <p className="text-orange-600 text-xs mt-2">
                  ⚠️ {t('taxResidency.detectionMayBeInaccurate', 'Automatische Erkennung kann ungenau sein. Bitte wählen Sie Ihr korrektes Land.')}
                </p>
              );
            }
            return null;
          })()}
        </div>

        {/* Detected Location Info */}
        {detectedCountry && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">
                {t('taxResidency.detectedLocation', 'Erkannter Standort')}:
              </span>{' '}
              {countries.find(c => c.code === detectedCountry)?.flag}{' '}
              {countries.find(c => c.code === detectedCountry)?.name || detectedCountry}
            </p>
          </div>
        )}

        {/* Country Selection */}
        <div className="mb-4 sm:mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('taxResidency.selectCountry', 'Wählen Sie Ihr Land')}
          </label>
          <p className="text-xs text-gray-500 mb-3">
            {t('taxResidency.selectCountryHint', 'Vorauswahl basiert auf Browser-Einstellungen. Bitte korrigieren Sie bei Bedarf.')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            {countries.map((country) => (
              <button
                key={country.code}
                type="button"
                onClick={() => setSelectedCountry(country.code)}
                disabled={loading}
                className={`
                  p-3 sm:p-4 rounded-lg border-2 transition-all duration-200 text-left
                  ${selectedCountry === country.code
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }
                  ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <span className="text-2xl sm:text-3xl">{country.flag}</span>
                  <span className="font-medium text-gray-900 text-sm sm:text-base">{country.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Important Info */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="text-xs sm:text-sm text-yellow-800">
              <p className="font-semibold mb-1">
                {t('taxResidency.important', 'Wichtig')}
              </p>
              <p>
                {t('taxResidency.importantText', 'Diese Einstellung bestimmt, welche Steuergesetze für Ihre Berechnungen verwendet werden. Sie können dies später in den Einstellungen ändern.')}
              </p>
              {selectedCountry === 'OTHER' && (
                <p className="mt-2 font-semibold text-orange-700">
                  {t('taxResidency.otherCountryWarning', 'Hinweis: Für Ihr Land sind keine spezifischen Steuerberechnungen verfügbar. Die App verwendet generische Einstellungen.')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Confirm Button */}
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!selectedCountry || loading}
          className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium text-sm sm:text-base
                   hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed
                   transition-colors duration-200 flex items-center justify-center
                   focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('taxResidency.confirming', 'Wird bestätigt...')}
            </>
          ) : (
            t('taxResidency.confirm', 'Bestätigen')
          )}
        </button>

        {/* Footer Note */}
        <p className="text-xs text-gray-500 text-center mt-4">
          {t('taxResidency.footer', 'Ihre Daten werden sicher gespeichert und nur für steuerliche Berechnungen verwendet.')}
        </p>
      </div>
    </div>
  );
}

export default TaxResidencyModal;
